import { Component, Input } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { ISubmitSdBallotVoteRequest, ISdVoteDto } from '../../shared/models/sd/sd-vote';
import { SdBallotService } from '../../shared/services/sd-ballot.service';
import { sdVoteOption, SdVoteId } from '../../../../shared/constants/sd-ballot.constants';
import { IApiResponse } from '../../../../shared/models/api-response';
import { IAppFeaturesSettings } from '../../../../shared/models/app-features-settings';
import { IAppUrls } from '../../../../shared/models/app-urls';
import { IUser } from '../../../../shared/models/user';
import { AuthService } from '../../../../shared/services/auth.service';
import { ConfigurationService } from '../../../../shared/services/configuration.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { UserUtility } from '../../../../shared/utilities/user-utility';
import { CommitteeUserService } from '../../../committees/shared/services/committee-user.service';


@Component({
    selector: 'app-sd-place-vote',
    templateUrl: './sd-place-vote.component.html',
    styleUrl: './sd-place-vote.component.scss',
    standalone: false
})


export class SdPlaceVoteComponent {

  @Input() userVote!: ISdVoteDto;
  @Input() user!: IUser;

  subscriptions: Subscription = new Subscription();

  voteOptionForm = new FormGroup({
    agreeSelectCtrl: new FormControl(false),
    disagreeSelectCtrl: new FormControl(false),
    certificateOfAppreciationCtrl: new FormControl(''),
    agreeNoteCtrl: new FormControl(''),
    disagreeNoteCtrl: new FormControl('')
  });

  userAddress!: string;

  isFormValid: boolean = false;
  isOktaUser: boolean = false;
  isExternalNonOktaUser : boolean = false;
  isExternalOktaUser : boolean = false;

  noteTitle = "Please enter a reason for your decision";

  agree = sdVoteOption["VT_AGREE"];
  disagree = sdVoteOption["VT_DISAGREE"];

  appUrls: IAppUrls | null = null;
  isInternalUser : boolean = false;
  isCoaManagedBySf?: boolean;

  constructor(
              private _authService: AuthService,
              private activeModal: NgbActiveModal,
              private _sdBallotService: SdBallotService,
              private _toastService: ToastService,
              private _committeeUserService: CommitteeUserService,
              private _configService: ConfigurationService,
            )
              {}

  get agreeSelect(){
    return this.voteOptionForm.get('agreeSelectCtrl');
  }
  get disagreeSelect() {
    return this.voteOptionForm.get('disagreeSelectCtrl');
  }

  get certificateOfAppreciation() {
    return this.voteOptionForm.get('certificateOfAppreciationCtrl');
  }
  get agreeNote() {
    return this.voteOptionForm.get('agreeNoteCtrl');
  }
  get disagreeNote() {
    return this.voteOptionForm.get('disagreeNoteCtrl');
  }


  ngOnInit(){
    this.isOktaUser = this._authService.isOktaUser;
    this.loadAppUrls();
    this.loadAppFeatures();

    this.loadUserDetails();

    this.loadExistingVote();

    this.voteOptionForm.valueChanges.subscribe(values => {
      this.validate();
    });


  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  loadAppFeatures() {
    const appFeaturesSubscription = this._configService.getAppFeaturesSettings()
    .subscribe({
      next: (appFeaturesSettingsResponse: IApiResponse<IAppFeaturesSettings>) => {
        if (appFeaturesSettingsResponse?.success && appFeaturesSettingsResponse.content) {
          this.isCoaManagedBySf = appFeaturesSettingsResponse.content.coaManagedInSf;
        }
      },
      error: (error) => {
        // TODO: Log to SumoLogic;
        console.error('SdPlaceVote: Error loading app features settings', error);
      }
    });

    this.subscriptions.add(appFeaturesSubscription);
  }

  loadAppUrls() {
    const appUrlSubscription = this._configService.getAppUrls()
    .subscribe({
      next: (appUrls) => {
        if (appUrls) {
          this.appUrls = appUrls;
        }
      },
      error: (error) => {
        // TODO: Log to SumoLogic;
      }
    });

    this.subscriptions.add(appUrlSubscription);
  }

  loadUserDetails() {

    this._committeeUserService.getCurrentUserContactDetails().subscribe((result) => {
      let committeeDetails = result.content;
      if (committeeDetails.address2Line1) {
        this.userAddress = this.formatAddress(committeeDetails.address2Line1) +
          this.formatAddress(committeeDetails.address2Line2) +
          this.formatAddress(committeeDetails.address2Line3) +
          this.formatAddress(committeeDetails.address2City) +
          this.formatAddress(committeeDetails.address2State) +
          this.formatAddress(committeeDetails.address2PostalCode);
      }
      else {
        this.userAddress = this.formatAddress(committeeDetails.address1Line1) +
          this.formatAddress(committeeDetails.address1Line2) +
          this.formatAddress(committeeDetails.address1Line3) +
          this.formatAddress(committeeDetails.address1City) +
          this.formatAddress(committeeDetails.address1State) +
          this.formatAddress(committeeDetails.address1PostCode);
      }
    });

     if (!this.user) {
      return;
    }
    this.isInternalUser = UserUtility.isInternalUser(this.user);
    this.updateUserFlags();
  }

  private formatAddress(address: string): string {
    if (address) {
      return " " + address;
    }
    return "";
  }

  loadExistingVote() {

    if (!this.userVote) return;

    if (this.userVote?.voteId == SdVoteId.VT_AGREE) {
      this.agreeSelect?.setValue(true);
      this.agreeNote?.setValue(this.userVote?.comment);
    }

    if (this.userVote?.voteId == SdVoteId.VT_DISAGREE) {
      this.disagreeSelect?.setValue(true);
      this.disagreeNote?.setValue(this.userVote?.comment);
    }

    this.certificateOfAppreciation?.setValue(this.userVote?.isCoa != null ? this.userVote?.isCoa == true ? 'yes' : 'no' : '');
  }

  updateUserFlags() {
    this.isExternalNonOktaUser = !this.isInternalUser && !this.isOktaUser;
    this.isExternalOktaUser = !this.isInternalUser && this.isOktaUser;
  }

  selectAgree() {
    this.reset();
    this.agreeSelect?.setValue(true);
  }

  selectDisagree() {
    this.reset();
    this.disagreeSelect?.setValue(true);
  }

  reset() {
    this.agreeSelect?.setValue(false);
    this.disagreeSelect?.setValue(false);
  }

  close() {
    this.activeModal.close('cancel');
  }

  dismiss(vote: string) {
    this.activeModal.dismiss(vote);
  }

  placeVote() {

    let vote: ISubmitSdBallotVoteRequest = this.getUserVote();

    this.subscriptions.add(this._sdBallotService.placeVote(vote).subscribe({
      next: (response: IApiResponse<any>) => {
        this._toastService.showSuccess("Your vote has been submitted. Thank you!", "Vote Successful");
        this.dismiss(vote.voteResult);
      },
      error: (error) => {
        this._toastService.showError("Your vote was not placed due to an unexpected error. Please try again or contact IT Support if issue persists.", "Vote Failed");
      }
    }));

  }

  getUserVote() : ISubmitSdBallotVoteRequest {

    return  {
      ballotId: this.userVote?.ballotRecordId,
      voteResult: this.agreeSelect?.value ?  SdVoteId.VT_AGREE : SdVoteId.VT_DISAGREE,
      ballotVoteId : this.userVote?.ballotVoteId,
      ballotComment: this.agreeSelect?.value ? this.agreeNote?.value??'' : this.disagree ? this.disagreeNote?.value??'' : '',
      isCOA: this.certificateOfAppreciation?.value == 'yes'
    }

  }

  validate() {
    this.isFormValid = (
        ((this.agreeSelect?.value) ||
        (this.disagreeSelect?.value && this.disagreeNote?.value != ''))
        )??false;
  }
}
